import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { PageContainer } from "../../components/page_container/PageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { selectState } from "./SideMenuSlice";
import { useLocation } from "react-router";
import React, { useEffect, useState } from "react";
import {
  getLanguageUrl,
  getLanguageFromUrl,
} from "../../utilities/languageUrls";
import { useTranslation } from "react-i18next";
import FunctionIcon from "@mui/icons-material/FunctionsOutlined";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeartOutlined";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import WeightIcon from "@mui/icons-material/ScaleOutlined";
import CompareIcon from "@mui/icons-material/CompareOutlined";
import HeatIcon from "@mui/icons-material/ThermostatOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUpOutlined";
import AssistantIcon from "@mui/icons-material/AssistantOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCardOutlined";
import AspectRatioIcon from "@mui/icons-material/AspectRatioOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdfOutlined";
import FolderZipIcon from "@mui/icons-material/FolderZipOutlined";
import QrCodeIcon from "@mui/icons-material/QrCodeOutlined";
import BarcodeIcon from "@mui/icons-material/VerticalShadesClosedOutlined";
import CsvJsonIcon from "@mui/icons-material/DataObjectOutlined";
import ImageCropperIcon from "@mui/icons-material/CropOutlined";
import EmailSignatureIcon from "@mui/icons-material/EmailOutlined";
import ImageToPdfIcon from "@mui/icons-material/PhotoAlbumOutlined";
import InvoiceIcon from "@mui/icons-material/ReceiptLongOutlined";
import PasswordIcon from "@mui/icons-material/PasswordOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBookOutlined";
import TextFieldsIcon from "@mui/icons-material/TextFieldsOutlined";
import TimerIcon from "@mui/icons-material/TimerOutlined";
import EventIcon from "@mui/icons-material/EventOutlined";
import AccessTimeIcon from "@mui/icons-material/UpdateOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import * as changeCase from "change-case";
import packageJson from "../../../package.json";
import styles from "./SideMenu.module.css";
import { LanguageSelector } from "../../components/language_selector/LanguageSelector";

import logo from "../../assets/LogoSmall.webp";
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Link } from "react-router-dom";
import { SideMenuLink } from "../../components/side_menu_link/SideMenuLink";

const drawerWidth = 240;

interface SideMenuProperties {
  pageTitle: string;
  children?: React.ReactNode;
}

// Category data structure matching the home page
interface ToolCategory {
  title: string;
  tools: Array<{
    label: string;
    link: string;
    icon: React.ReactNode;
  }>;
}

export function SideMenu(properties: SideMenuProperties) {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | false>(
    false
  );
  const location = useLocation();
  const currentLanguage = getLanguageFromUrl(location.pathname);

  // Function to generate language-specific URLs for tools
  const getToolUrl = (toolKey: string) =>
    getLanguageUrl(currentLanguage, toolKey);

  // Generate tool categories with language-specific URLs
  const toolCategories: ToolCategory[] = [
    {
      title: t("sideMenu.categories.fileMediaTools"),
      tools: [
        {
          label: t("sideMenu.tools.pdfMerger"),
          link: getToolUrl("pdfMerger"),
          icon: (
            <PictureAsPdfIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.pdfCompression"),
          link: getToolUrl("pdfCompression"),
          icon: (
            <FolderZipIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.imageConverter"),
          link: getToolUrl("imageConverter"),
          icon: (
            <CompareIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.mediaCompression"),
          link: getToolUrl("compression"),
          icon: (
            <FolderZipIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.androidIosResizer"),
          link: getToolUrl("androidIosResizer"),
          icon: (
            <AspectRatioIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.imageCropper"),
          link: getToolUrl("imageCropper"),
          icon: (
            <ImageCropperIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.imageToPdf"),
          link: getToolUrl("imageToPdf"),
          icon: (
            <ImageToPdfIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
      ],
    },
    {
      title: t("sideMenu.categories.webDevelopmentTools"),
      tools: [
        {
          label: t("sideMenu.tools.faviconGenerator"),
          link: getToolUrl("faviconGenerator"),
          icon: (
            <AssistantIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.qrCodeGenerator"),
          link: getToolUrl("qrCodeGenerator"),
          icon: (
            <QrCodeIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.barcodeGenerator"),
          link: getToolUrl("barcodeGenerator"),
          icon: (
            <BarcodeIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
      ],
    },
    {
      title: t("sideMenu.categories.documentGenerators"),
      tools: [
        {
          label: t("sideMenu.tools.emailSignature"),
          link: getToolUrl("emailSignature"),
          icon: (
            <EmailSignatureIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.invoiceGenerator"),
          link: getToolUrl("invoiceGenerator"),
          icon: (
            <InvoiceIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
      ],
    },
    {
      title: t("sideMenu.categories.dateTimeTools"),
      tools: [
        {
          label: t("sideMenu.tools.countdownTimer"),
          link: getToolUrl("countdownTimer"),
          icon: (
            <TimerIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.dateCalculator"),
          link: getToolUrl("dateCalculator"),
          icon: (
            <EventIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.timezoneConverter"),
          link: getToolUrl("timezoneConverter"),
          icon: (
            <AccessTimeIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
      ],
    },
    {
      title: t("sideMenu.categories.dataTextTools"),
      tools: [
        {
          label: t("sideMenu.tools.csvJsonConverter"),
          link: getToolUrl("csvJsonConverter"),
          icon: (
            <CsvJsonIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.passwordGenerator"),
          link: getToolUrl("passwordGenerator"),
          icon: (
            <PasswordIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.loremIpsum"),
          link: getToolUrl("loremIpsum"),
          icon: (
            <MenuBookIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.caseConverter"),
          link: getToolUrl("caseConverter"),
          icon: (
            <TextFieldsIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.uuidGenerator"),
          link: getToolUrl("uuidGenerator"),
          icon: (
            <TextFieldsIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.base64Encoder"),
          link: getToolUrl("base64Encoder"),
          icon: (
            <TextFieldsIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
              {
        label: t("sideMenu.tools.jwtDecoder"),
        link: getToolUrl("jwtDecoder"),
        icon: (
          <TextFieldsIcon
            sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
          />
        ),
      },
      {
        label: t("sideMenu.tools.jsonToCSharp"),
        link: getToolUrl("jsonToCSharp"),
        icon: (
          <TextFieldsIcon
            sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
          />
        ),
      },
      ],
    },
    {
      title: t("sideMenu.categories.unitConverters"),
      tools: [
        {
          label: t("sideMenu.tools.baseConverter"),
          link: getToolUrl("baseConverter"),
          icon: (
            <FunctionIcon
              sx={{ color: "#FFFFFF", height: "27px", width: "27px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.distanceConverter"),
          link: getToolUrl("distanceConverter"),
          icon: (
            <SquareFootIcon
              sx={{ color: "#FFFFFF", height: "27px", width: "27px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.weightConverter"),
          link: getToolUrl("weightConverter"),
          icon: (
            <WeightIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.temperatureConverter"),
          link: getToolUrl("temperatureConverter"),
          icon: (
            <HeatIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
      ],
    },
    {
      title: t("sideMenu.categories.calculators"),
      tools: [
        {
          label: t("sideMenu.tools.bmiCalculator"),
          link: getToolUrl("bmiCalculator"),
          icon: (
            <MonitorHeartIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.interestCalculator"),
          link: getToolUrl("interestCalculator"),
          icon: (
            <TrendingUpIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
        {
          label: t("sideMenu.tools.creditCardCalculator"),
          link: getToolUrl("creditCardCalculator"),
          icon: (
            <CreditCardIcon
              sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
            />
          ),
        },
      ],
    },
  ];

  // Find which category contains the current page
  const findCurrentPageCategory = (currentPath: string): string | null => {
    for (const category of toolCategories) {
      const hasCurrentPage = category.tools.some(
        (tool) => tool.link === currentPath
      );
      if (hasCurrentPage) {
        return category.title;
      }
    }
    return null;
  };

  // Auto-expand the category for the current page
  useEffect(() => {
    const currentCategory = findCurrentPageCategory(location.pathname);
    if (currentCategory) {
      setExpandedCategory(currentCategory);
    }
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleAccordionChange =
    (categoryTitle: string) =>
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedCategory(isExpanded ? categoryTitle : false);
    };

  // Helper function to create icon with conditional styling
  const createIconWithActiveState = (
    icon: React.ReactNode,
    isActive: boolean
  ) => {
    return React.cloneElement(icon as React.ReactElement, {
      sx: {
        ...(icon as React.ReactElement).props.sx,
        color: isActive ? "#FFE4B5" : "#FFFFFF",
        height: (icon as React.ReactElement).props.sx?.height || "23px",
        width: (icon as React.ReactElement).props.sx?.width || "23px",
      },
    });
  };

  const drawer = (
    <Box>
      <Box sx={{ mt: 1 }} />
      <Link to={`/${currentLanguage}`}>
        <img
          src={logo}
          loading="lazy"
          width={120}
          height={115}
          alt="Number Chomper logo"
          className="applogo"
          title="Free, Fast and Functional Online Converters / Generators"
        />
      </Link>
      <Divider />
      <Box sx={{ color: "#FFFFFF" }}>
        {toolCategories.map((category, categoryIndex) => {
          const isExpanded = expandedCategory === category.title;

          return (
            <Accordion
              key={category.title}
              expanded={isExpanded}
              onChange={handleAccordionChange(category.title)}
              sx={{
                backgroundColor: "transparent",
                color: "#FFFFFF",
                boxShadow: "none",
                "&:before": {
                  display: "none",
                },
                "& .MuiAccordionSummary-root": {
                  minHeight: "48px",
                  padding: "0 16px",
                },
                "& .MuiAccordionSummary-content": {
                  margin: "8px 0",
                },
                "& .MuiAccordionDetails-root": {
                  padding: "0 16px 8px 16px",
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon sx={{ color: "#FFE4B5", fontSize: "20px" }} />
                }
                sx={{
                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#FFE4B5",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  {category.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                {category.tools.map((tool) => {
                  const isActive = location.pathname === tool.link;

                  return (
                    <SideMenuLink
                      key={tool.link}
                      label={tool.label}
                      link={tool.link}
                      onClick={() => {
                        setMobileOpen(false);
                      }}
                      icon={createIconWithActiveState(tool.icon, isActive)}
                      isActive={isActive}
                    />
                  );
                })}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "#282b3900",
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar
          className={styles.appbar}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              color="inherit"
              aria-label={t("sideMenu.toolbar.openDrawer")}
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: "none" } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              {properties.pageTitle}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <LanguageSelector />
            <Link
              to="/AboutUs"
              title="About NumberChomper.com"
              style={{ textDecoration: "none" }}
            >
              <Button
                color="inherit"
                onClick={() => {}}
                sx={{ display: { xs: "none", sm: "block" } }}
              >
                {t("sideMenu.toolbar.aboutUs")}
              </Button>
            </Link>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
          <Divider />

          {/* Language selector in mobile drawer */}
          <Box sx={{ p: 2, display: { xs: "block", sm: "none" } }}>
            <Typography
              variant="body2"
              sx={{ color: "#FFE4B5", fontWeight: 600, mb: 1 }}
            >
              {t("sideMenu.toolbar.language")}
            </Typography>
            <LanguageSelector />
          </Box>

          <Divider sx={{ display: { xs: "block", sm: "none" } }} />

          {/* About Us link in mobile drawer */}
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <SideMenuLink
              label={t("sideMenu.toolbar.aboutUs")}
              link="/AboutUs"
              onClick={() => {
                setMobileOpen(false);
              }}
              icon={
                <InfoIcon
                  sx={{ color: "#FFFFFF", height: "23px", width: "23px" }}
                />
              }
              isActive={location.pathname === "/AboutUs"}
            />
          </Box>

          <Divider sx={{ display: { xs: "block", sm: "none" } }} />

          <Box className="versionDiv" sx={{ mt: 2, marginTop: "auto" }}>
            <label>
              {t("sideMenu.version")}: {packageJson.version}
            </label>
          </Box>
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
          <Divider />

          <Box className="versionDiv" sx={{ mt: 2, marginTop: "auto" }}>
            <label>
              {t("sideMenu.version")}: {packageJson.version}
            </label>
          </Box>
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {properties.children}
      </Box>
    </Box>
  );
}
